/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { useField, useForm } from 'react-final-form-hooks';

import { actions } from '@/redux/av-check';
import AlertBox from '@/components/av-check/AlertBox';
import Autocomplete from '@/components/av-check/Autocomplete';
import Instruction from '@/components/av-check/Instruction';
import {
  AddressType,
  AvCheckFormData,
  CmsPageContentIdentifier,
  CmsPageContentType,
  Feature,
  HouseholdConfigurationType,
  ReduxStateType,
  ResultType,
  UsageType,
} from '@/types/av-check';
import { composeValidators, setNativeValue, validateRegexp, validateRequired } from '@/utils';
import ResultLoadingOverlay from '@/components/av-check/ResultLoadingOverlay';
import HouseholdConfigurator from '@/components/av-check/HouseholdConfigurator/HouseholdConfigurator';

export interface StateProps {
  cmsContent: CmsPageContentType
  address: AddressType
  householdConfig: HouseholdConfigurationType
  result?: ResultType
  resultError: boolean
  cityList?: string[]
  streetList?: string[]
  isCciTheme: boolean
  enabledFeatures?: Feature[]
}

export interface DispatchProps {
  setCityList: Function
  setStreetList: Function
}

export interface AvailabilityCheckerProps {
  isPdpCall?:boolean
  avCheckPending?: boolean
  withPendingStyle?: boolean
  onAvailabilityCheck: (requestBody: AvCheckFormData) => void
  onCityListFetch: (zipCode: string, onSuccess: (streets: string[]) => void) => Promise<any>
  onStreetListFetch:
  (zipCode: string, city: string, onSuccess: (streets: string[]) => void) => Promise<any>
  displayHouseholdConfigurator: boolean
  showDTAGSpecificationView?: boolean
}

const AvailabilityChecker = ({
  cmsContent,
  address,
  result,
  householdConfig,
  resultError,
  cityList,
  streetList,
  avCheckPending = false,
  withPendingStyle = false,
  onAvailabilityCheck,
  onCityListFetch,
  onStreetListFetch,
  setCityList,
  setStreetList,
  displayHouseholdConfigurator,
  enabledFeatures,
  isPdpCall,
  isCciTheme,
  showDTAGSpecificationView = false,
}: StateProps & AvailabilityCheckerProps & DispatchProps) => {
  const {
    form, handleSubmit, invalid, pristine,
  } = useForm({
    onSubmit: onAvailabilityCheck,
    initialValues: {
      zipCode: address.zipCode,
      city: address.city,
      houseNumber: address.houseNumber,
      street: address.street,
      corrected: address.corrected,
      usageTypeOnlineGaming: householdConfig.usageTypes.includes(UsageType.GAMING),
      usageTypeVideoStreaming: householdConfig.usageTypes.includes(UsageType.STREAMING),
      usageTypeHomeOffice: householdConfig.usageTypes.includes(UsageType.HOME_OFFICE),
      numberOfPersons: householdConfig.personInHousehold === 0 || householdConfig.personInHousehold === undefined ? 'no-selection' : householdConfig.personInHousehold.toString(),
    },
  });
  const zipCode = useField('zipCode', form, composeValidators(
    validateRequired(cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageZipCodeRequired]),
    validateRegexp(/\d{5}/, cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageZipCodeInvalid]),
  ));
  const city = useField('city', form, validateRequired(cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageCityRequired]));
  const street = useField('street', form, validateRequired(cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageStreetRequired]));
  const houseNumber = useField('houseNumber', form, composeValidators(
    validateRequired(cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageHouseNumberRequired]),
    validateRegexp(/^(\d{1,4})([^0-9].*)?$/, cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageHouseNumberInvalid]),
  ));

  const zipCodeRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const streetRef = useRef<HTMLInputElement>(null);
  const houseNumberRef = useRef<HTMLInputElement>(null);

  const [forceShowCitySuggestions, setForceShowCitySuggestions] = useState(false);
  const [forceShowStreetSuggestions, setForceShowStreetSuggestions] = useState(false);

  const onAutocompleteEditing = (name: string) => {
    if (name === 'city' && streetList) {
      if (streetRef.current) {
        setNativeValue('', streetRef.current);
        streetRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
        setStreetList(undefined);
      }
    }
    if (name === 'street' && houseNumberRef.current) {
      setNativeValue('', houseNumberRef.current);
      houseNumberRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const onComplete = (name: string, value: string) => {
    if (name === 'city' && cityRef.current && zipCodeRef.current) {
      cityRef.current?.focus();
      setNativeValue(value, cityRef.current);
      cityRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
      const onStreetsRequestSuccess = (streets: string[]) => {
        setStreetList(streets);
        cityRef.current?.blur();
        streetRef.current?.focus();
        setForceShowStreetSuggestions(true);
      };
      onStreetListFetch(zipCodeRef.current?.value, value, onStreetsRequestSuccess);
    }

    if (name === 'street' && streetRef.current) {
      setNativeValue(value, streetRef.current);
      streetRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
      houseNumberRef.current?.focus();
    }
  };

  const onZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    zipCode.input.onChange(e);
    const { target } = e;
    if (!zipCode.meta.error) {
      if (cityRef.current) {
        setNativeValue('', cityRef.current);
        cityRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
        setCityList(undefined);
      }
      if (streetRef.current) {
        setNativeValue('', streetRef.current);
        streetRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
        setStreetList(undefined);
      }
    }

    if (target.value.length === 5 && target.value.match(/\d{5}/) && !city.input.value) {
      target.blur();
      const onCitiesRequestSuccess = (cities: string[]) => {
        setCityList(cities);
        if (cities && cities.length === 1 && cityRef.current) {
          onComplete('city', cities[0]);
        } else {
          setForceShowCitySuggestions(true);
          cityRef.current?.focus();
        }
      };
      onCityListFetch(target.value, onCitiesRequestSuccess);
    }
  };

  /**
   * Determines whether the check button should be disabled. This is the case
   * if:
   *
   * * Any of the input fields has failed validation (``invalid=true``).
   * * The form is 'pristine' (meaning none of the default values were changed
   *   by the user), **except** if ``address.corrected==true`` (meaning the
   *   backend returned a corrected address. In this case, we want to give the
   *   user the possibility to verify the new address and resubmit it).
   *
   * Introduced to fix [OSD-6603: Availability check not possible after adress
   * has been corrected](https://www.sg.de.o2.com/jira/browse/OSD-6603)
   *
   * @return ``true`` if the button should be disabled.
   */
  function shouldDisableCheckButton() {
    return invalid || pristine && !address.corrected;
  }

  const isAvCheckFloatLabelToggleEnabled = enabledFeatures?.includes(Feature.AVCHECK_FLOAT_LABELS);
  const [width, setWidth] = React.useState({
    width: window.innerWidth,
  });

  function placeHolderValue() {
    const MOBILE_VIEW_WINDOW_SIZE = 768;
    return (width.width <= MOBILE_VIEW_WINDOW_SIZE && isAvCheckFloatLabelToggleEnabled) ?
      cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderHouseNumberMobileView] :
      cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderHouseNumber];
  }

  function changePlaceHolderValue() {
    $('#houseNumber').attr('placeholder', placeHolderValue());
  }

  React.useEffect(() => {
    function handleResize() {
      setWidth({
        width: window.innerWidth,
      });
      changePlaceHolderValue();
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const isDtagGuiEnhancementsToggleEnabled = enabledFeatures ? (
    isCciTheme ?
      enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS_CCI)
      : enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS)) : false;
  return (
      <>
        <style>{styleSet}</style>
    <div style={{ position: 'relative' }}>
      {withPendingStyle && avCheckPending && (
        <ResultLoadingOverlay
          stylesOverride={{
            position: 'absolute', top: '50%', left: '0', zIndex: 100, transform: 'translateY(-50%)', width: '100%',
          }}
          title={isDtagGuiEnhancementsToggleEnabled && showDTAGSpecificationView ? cmsContent[CmsPageContentIdentifier.DtagFtthLoadingOverlayTitle] :
            cmsContent[CmsPageContentIdentifier.LoadingOverlayTitle]}
          message={cmsContent[CmsPageContentIdentifier.LoadingOverlayMessage]}
        />
      )}
      <div className="article" style={withPendingStyle && avCheckPending ? { opacity: 0.1 } : {}}>
        <div className="form-title table-title text-right">
          <span className="title-info" dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.FormFieldRequiredHint] }} />
        </div>
        {/*
        wait until cms content is populated, otherwise form is being rendered with empty
        error messages
      */}
        {
          cmsContent[CmsPageContentIdentifier.FormFieldErrorMessageZipCodeRequired] && (
          <form onSubmit={handleSubmit}>
            <style>style={( !isPdpCall ) ? style : 'none'}</style>
            <fieldset>
              <div className="form-group form-group-line">
                <div
                  className={`input required size-xs space-right ${zipCode.meta.error && zipCode.meta.touched ? 'has-error' : ''}`}
                >
                  <span className="form-floating js-float-label hide-label js-unhighlight-label">
                    <input
                      {...zipCode.input}
                      ref={zipCodeRef}
                      type="text"
                      autoComplete="section-address postal-code"
                      placeholder={cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderZipCode]}
                      className="form-control"
                      onChange={onZipcodeChange}
                      data-co-uitest-element="zip"
                    />
                    <label>{cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderZipCode]}</label>
                    {zipCode.meta.error && zipCode.meta.touched
                                && <span className="help-block">{zipCode.meta.error}</span>}
                  </span>
                </div>
                <div
                  className={`input required size-sm ${city.meta.error && city.meta.touched ? 'has-error' : ''}`}
                >
                  <span className="form-floating js-float-label hide-label js-unhighlight-label" >
                  <Autocomplete
                    innerRef={cityRef}
                    fieldProps={city}
                    inputProps={{ placeholder: cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderCity], autoComplete: 'section-address locality' }}
                    prefilled={cityRef.current?.value !== undefined}
                    suggestions={cityList}
                    forceShowSuggestions={forceShowCitySuggestions}
                    onComplete={(name, value) => onComplete(name, value)}
                    onEditting={onAutocompleteEditing}
                    testElementId="city"
                  />
                    <label>{cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderCity]}</label>
                    </span>
                </div>
              </div>
              <div className="form-group form-group-line">
                <div
                  className={`input required size-sm space-right ${street.meta.error && street.meta.touched ? 'has-error' : ''}`}
                >
                  <span className="form-floating js-float-label hide-label js-unhighlight-label" >
                  <Autocomplete
                    innerRef={streetRef}
                    fieldProps={street}
                    inputProps={{ placeholder: cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderStreet], autoComplete: 'section-address address-line1' }}
                    prefilled={streetRef.current?.value !== undefined}
                    suggestions={streetList}
                    forceShowSuggestions={forceShowStreetSuggestions}
                    onComplete={(name, value) => onComplete(name, value)}
                    onEditting={onAutocompleteEditing}
                    testElementId="street"
                  />
                    <label>{cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderStreet]}</label>
                  </span>
                </div>
                <div
                  className={`input required size-xs ${houseNumber.meta.error && houseNumber.meta.touched ? 'has-error' : ''}`}
                >
                  <span className="form-floating js-float-label hide-label js-unhighlight-label">
                    <input
                      {...houseNumber.input}
                      id='houseNumber'
                      type="text"
                      ref={houseNumberRef}
                      placeholder={isPdpCall ? cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderHouseNumber] : placeHolderValue()}
                      className="form-control"
                      autoComplete="section-address address-line2"
                      data-co-uitest-element="houseNumber"
                    />
                    <label>{isPdpCall ? cmsContent[CmsPageContentIdentifier.FormFieldPlaceholderHouseNumber] : placeHolderValue()}</label>
                    {houseNumber.meta.error && houseNumber.meta.touched
                                && <span className="help-block">{houseNumber.meta.error}</span>}
                  </span>
                </div>
              </div>
            </fieldset>
            {/* eslint-disable no-nested-ternary, @typescript-eslint/indent, operator-linebreak */}
            {
                                (resultError) ? (
                                  <AlertBox
                                    contentIdentifier={CmsPageContentIdentifier.ErrorMessageServerError}
                                    type="danger"
                                  />
                                    ) :
                                    (result && !result.address) ? (
                                      <AlertBox
                                        contentIdentifier={CmsPageContentIdentifier.ErrorMessageNoMatchingAddress}
                                        type="warning"
                                      />
                                        ) :
                                        ((result && result.address.corrected)) ? (
                                          <AlertBox
                                            contentIdentifier={CmsPageContentIdentifier.InfoMessageCorrectedAddress}
                                            type="info"
                                          />
                                            ) :
                                            ((result && result.tariffOffers.length === 0) ? (
                                              <AlertBox
                                                contentIdentifier={CmsPageContentIdentifier.ErrorMessageNoTariffOffers}
                                                type="warning"
                                              />
                                                ) :
                                                null)
                            }
            {/* eslint-enable no-nested-ternary, @typescript-eslint/indent, operator-linebreak */}
            <div className="form-group text-right">
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <button
                className={`btn btn-alpha ${shouldDisableCheckButton() ? 'disabled' : ''}`}
                disabled={shouldDisableCheckButton()}
                type="submit"
                dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.HomeAvailabilityCheckButtonText] }}
              />
            </div>
            {(displayHouseholdConfigurator) && (
            <>
              <HouseholdConfigurator form={form} />
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <button
                className={`btn btn-alpha ${shouldDisableCheckButton() ? 'disabled' : ''}`}
                disabled={shouldDisableCheckButton()}
                type="submit"
                dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.HomeAvailabilityCheckButtonText] }}
              />
            </>
            )}
          </form>
          )
                }
        <Instruction />
      </div>
    </div>
      </>
  );
};

const mapStateToProps = (state: ReduxStateType) => ({
  cmsContent: state.cmsContent,
  address: state.address,
  householdConfig: state.householdConfig,
  resultError: state.resultError,
  result: state.result,
  cityList: state.cityList,
  streetList: state.streetList,
  enabledFeatures: state.enabledFeatures,
  isCciTheme: state.meta?.cciTheme || false,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setCityList: (cities: string[] | undefined) => dispatch(actions.setCityList(cities)),
  setStreetList: (streets: string[] | undefined) => dispatch(actions.setStreetList(streets)),
});

const style = `
  .form-group-line .input.size-xs {
  width: inherit;
}

@media (max-width: 767px) {
  .form-group-line .input.size-xs {
    width: 33%;
    
  }
  .form-group-line > .input {
    margin-bottom: 0px;
  }
  .form-group-line .input.size-sm {
    width: 66%;
    
  }
}
.form-group-line {
  margin-bottom: 10px;
  display: flex;
  padding-top: 0px;
  webkit-box-flex: 1;
  webkit-flex: 1 1 auto;
  flex: 1 1 auto;
}
.space-right {
  margin-right: 10px;
}
  `;

const styleSet = `
.form-floating>.form-control:focus, .form-floating>.form-control:not(:placeholder-shown) {
    padding-top: 1.625rem;
    padding-bottom: 0.625rem;
}
.form-floating>.form-control:focus~label, .form-floating>.form-control:not(:placeholder-shown)~label, .form-floating>.form-select~label {
    opacity: .65;
    transform: scale(.85) translateY(-1rem) translateX(0.15rem);
}

.form-floating>label { 
    font-family: inherit !important;
    position: absolute !important;
    color:gray;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 1rem 0.75rem;
    overflow: hidden;
    text-align: start;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
    border: 1px solid transparent;
    transform-origin: 0 0;
    transition: opacity .1s ease-in-out,transform .1s ease-in-out;
}
.form-control::placeholder {
    opacity: 0;
}
.form-floating>.form-control {
    padding: 1rem 0.75rem;
    height: 3.4rem;
}
.form-control::-moz-placeholder {
    opacity: 0 !important;}
`;

export default connect(mapStateToProps, mapDispatchToProps)(AvailabilityChecker);
